import { PromptModal } from '@/components'
import { Input } from '@/components/ui'
import { ALL_WORKSHOPS_QUERY } from '@/modules/vehicles/queries'
import type { WorkshopNode } from '@/modules/vehicles/types.graphql'
import { getNodes } from '@/utils/graphql'
import { useQuery } from '@apollo/client'
import type { PromptResolve } from '@ur-solutions/react-components'
import { useTranslate } from '@ur-solutions/react-hooks'
import { useMemo, useState } from 'react'
import type { WorkshopForm } from './workshop-form'
import {
  emptyWorkshopForm,
  trimWorkshopForm,
  validateWorkshopForm,
} from './workshop-form'

interface WorkshopPromptProps {
  workshop?: WorkshopNode
  onSubmit: PromptResolve<WorkshopForm | null>
}

export const WorkshopPrompt: React.FC<WorkshopPromptProps> = ({
  workshop,
  onSubmit,
}) => {
  const translations = useTranslate({
    name: 'common.name',
    address: 'common.address',
    phone: 'common.phone',
    email: 'common.email',

    addWorkshop: 'settings.add-workshop',
    editWorkshop: 'settings.edit-workshop',

    create: 'common.create-alt',
    update: 'common.update',

    validation: {
      nameExists: 'settings.validation.workshop-name-exists',
      invalidEmail: 'error.valid-email-required',
    },
  })

  const { data } = useQuery(ALL_WORKSHOPS_QUERY, {
    variables: { orderBy: 'name' },
  })

  const takenNames = useMemo(
    () =>
      getNodes(data?.allWorkshops)
        .filter(other => other.id !== workshop?.id)
        .map(other => other.name.trim().toLowerCase()),
    [data, workshop?.id]
  )

  const [form, setForm] = useState<WorkshopForm>(() =>
    workshop
      ? {
          name: workshop.name,
          address: workshop.address,
          phone: workshop.phone,
          email: workshop.email,
        }
      : emptyWorkshopForm
  )

  const errors = validateWorkshopForm(form, takenNames)
  const valid = !errors.name && !errors.email

  function handleChange(field: keyof WorkshopForm) {
    return (value: string) =>
      setForm(current => ({ ...current, [field]: value }))
  }

  return (
    <PromptModal
      title={workshop ? translations.editWorkshop : translations.addWorkshop}
      submitText={workshop ? translations.update : translations.create}
      submitDisabled={!valid}
      cancelOnClickOutside
      cancelOnEscape
      onCancel={() => onSubmit(null)}
      onSubmit={() => onSubmit(trimWorkshopForm(form))}
    >
      <div className="flex flex-col gap-4">
        <Input
          label={translations.name}
          value={form.name}
          error={
            errors.name === 'taken' ? translations.validation.nameExists : null
          }
          autoFocus
          onChange={handleChange('name')}
        />
        <Input
          label={translations.address}
          value={form.address}
          onChange={handleChange('address')}
        />
        <Input
          label={translations.phone}
          type="tel"
          value={form.phone}
          maxLength={32}
          onChange={handleChange('phone')}
        />
        <Input
          label={translations.email}
          type="email"
          value={form.email}
          error={errors.email ? translations.validation.invalidEmail : null}
          onChange={handleChange('email')}
        />
      </div>
    </PromptModal>
  )
}
